import {
  ChangePasswordPayload,
  ChangePasswordResponse,
  ContactSupportResponse,
  StakeDetailsResponse,
  StakeOptions,
  UserInfoResponse,
  Wallet,
  UpdateUserProfile,
} from '@/types/UserDetails';
import { apiSlice } from '../api/apiSlice';
import { setsupportInfo, setUserInfo, setUserStake, setWalletInfo } from '../slices/userSlice';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';

export const userApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUserInfo: builder.query<UserInfoResponse, void>({
      query: () => ({
        url: '/users/me',
        method: 'GET',
      }),
      providesTags: ['User'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        // No redirects here. A first-login user (`passwordChanged: false`) gets the forced
        // Change Password popup from the Header, and an expired session is handled in apiSlice.
        try {
          const { data } = await queryFulfilled;
          dispatch(setUserInfo(data));
        } catch (error: any) {
          console.error('Failed to fetch user details:', error);
          if (error?.error?.status === 401) {
            Cookies.remove('access-token');
          }
        }
      },
    }),
    updateUserInfo: builder.mutation<UserInfoResponse, UpdateUserProfile>({
      query: (body) => ({
        url: '/users/me',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error) => (error ? [] : ['User']),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUserInfo(data));
        } catch (error) {
          console.error('Failed to fetch user details:', error);
        }
      },
    }),
    changePassword: builder.mutation<ChangePasswordResponse, ChangePasswordPayload>({
      query: (body) => ({
        url: '/users/me/change-password',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message, { id: 'change-password-success' });
        } catch (error: any) {
          console.error('Failed to fetch user details:', error);
        }
      },
      invalidatesTags: ['User'],
    }),

    // Get current user's wallets
    getUserWallets: builder.query<Wallet[], void>({
      query: () => ({
        url: `/users/me/wallets`,
        method: 'GET',
        params: { t: Date.now() },
      }),
      providesTags: ['Bets', 'User'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setWalletInfo(data));
        } catch (error) {
          console.error('Failed to fetch user details:', error);
        }
      },
    }),

    getContactSupport: builder.query<ContactSupportResponse, void>({
      query: () => ({
        url: '/contact-support',
        method: 'GET',
        params: { t: Date.now() },
      }),

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setsupportInfo(data));
        } catch (error) {
          console.error('Failed to fetch support details:', error);
        }
      },

      providesTags: ['Support'],
    }),

    getStakeDetails: builder.query<StakeDetailsResponse, void>({
      query: () => ({
        url: '/user/get-usermeta-details',
        method: 'GET',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUserStake(data?.data));
        } catch (error) {
          console.error('Failed to fetch user details:', error);
        }
      },
    }),
    updateStakeDetails: builder.mutation<StakeDetailsResponse, StakeOptions>({
      query: (payload) => ({
        url: '/user/update-chipssetting',
        method: 'PUT',
        body: { oneclick: payload },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUserStake(data?.data));
        } catch (error) {
          console.error('Failed to fetch user details:', error);
        }
      },
    }),
  }),
});

export const {
  useGetUserInfoQuery,
  useGetStakeDetailsQuery,
  useUpdateStakeDetailsMutation,
  useGetUserWalletsQuery,
  useGetContactSupportQuery,
  useChangePasswordMutation,
  useUpdateUserInfoMutation,
  useLazyGetUserWalletsQuery,
} = userApi;
